import {
  captureSupportScope,
  internalApiHeaders,
  originalAdministratorCleanupHeaders,
  type SupportBinding,
} from '../admin/supportTransport';
const API_BASE_URL = (import.meta.env.VITE_API_URL || '/api').replace(/\/$/, '');

export interface PracticeSummary {
  id: string;
  name: string;
  status: 'pending' | 'approved' | 'rejected' | 'suspended';
  role: 'owner' | 'staff';
  canManage: boolean;
  website?: string;
  billingEmail?: string;
  applicationNotes?: string;
  createdAt: string;
}
export interface PublicTeamMember {
  id?: string;
  name: string;
  role: string;
  biography: string;
  photoUrl?: string;
}
export interface AdviserProfileContent {
  firmName: string;
  headline: string;
  introduction: string;
  logoUrl: string;
  coverImageUrl: string;
  services: string[];
  specialities: string[];
  industries: string[];
  location: string;
  serviceAreas: string[];
  websiteUrl: string;
  linkedinUrl: string;
  contactEmail: string;
  contactPhone: string;
  bookingUrl: string;
  team: PublicTeamMember[];
}
export interface ProfileState {
  slug: string;
  draft: AdviserProfileContent;
  published: AdviserProfileContent | null;
  approvalStatus: 'pending' | 'approved' | 'rejected';
  reviewStage?: 'draft' | 'submitted' | 'approved' | 'changes_requested';
  publishedAt: string | null;
  updatedAt: string;
  reviewReason?: string;
}
export interface PublicAdviser extends AdviserProfileContent {
  slug: string;
  practiceId?: string;
  publishedAt?: string;
}
export interface PracticeMember {
  userId: string;
  id?: string;
  name: string;
  email: string;
  role: 'owner' | 'staff';
  createdAt?: string;
}
export interface PracticeInvitation {
  id: string;
  email: string;
  status: string;
  expiresAt: string;
}
export interface ClientSummary {
  relationshipId: string;
  companyId: string;
  name: string;
  industry: string;
  status: string;
  isDemo: boolean;
  ownershipStatus: string;
  tasksCompleted: number | null;
  totalTasks: number | null;
  objectivesCount: number | null;
  summaryErrors?: { progress?: string; billing?: string };
  billingStatus: 'demo' | 'not_started' | 'active' | 'trialing' | 'attention' | 'unknown';
  personalPlanningCoverage: boolean;
  billingComponents: Array<{
    component: 'planning' | 'pro';
    status: string;
    payerType: 'client' | 'practice';
    payerPracticeId: string | null;
    renewsAt: string | null;
  }>;
  handoffStatus?: string;
  ownerEmail?: string;
  planningProgress?: number;
  nextAction?: string;
  assignedUserIds?: string[];
  templateKey?: string;
}
export interface ClientLinkRequest {
  id: string;
  companyId: string;
  companyName: string;
  practiceName: string;
  status: string;
}
export interface CreateClientResult {
  companyId: string;
  linkId: string;
  acceptUrl: string;
  ownerInviteUrl: string;
}
export interface PracticeWorkspace {
  practice: PracticeSummary;
  clients: ClientSummary[];
  team: { members: PracticeMember[]; invitations: PracticeInvitation[] };
  profile: ProfileState | null;
}
export interface DirectoryResult {
  advisers: PublicAdviser[];
  total: number;
  page: number;
  limit: number;
}
export interface AdviserAdminData {
  practices: PracticeSummary[];
  profiles: Array<ProfileState & { practiceId: string; practiceName: string }>;
  featured: Array<{ practiceId: string; position: number }>;
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  isPublic = false,
  cleanupBinding?: SupportBinding
): Promise<T> {
  const requestScope = captureSupportScope();
  const clerk = (window as any).Clerk;
  const token = !isPublic && clerk?.session ? await clerk.session.getToken() : null;
  const isUpload = options.body instanceof FormData;
  const destination = `${API_BASE_URL}${path.replace(/^\/api(?=\/)/, '')}`;
  const headers = {
    ...(!isUpload && { 'Content-Type': 'application/json' }),
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };
  const response = await fetch(`${API_BASE_URL}${path.replace(/^\/api(?=\/)/, '')}`, {
    ...options,
    headers: cleanupBinding
      ? originalAdministratorCleanupHeaders(destination, headers, cleanupBinding)
      : internalApiHeaders(destination, headers, isPublic, requestScope),
  });
  if (!response.headers.get('content-type')?.includes('application/json'))
    throw new Error('The adviser service is temporarily unavailable. Please try again.');
  const result = await response.json();
  if (!response.ok || !result.success)
    throw new Error(result.error || 'Unable to complete this request.');
  return result.data as T;
}
export { request as adviserRequest };
export interface AdviserBillingCard {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
}
export interface AdviserBillingComponent {
  id: string;
  component: 'planning' | 'pro';
  payerType: 'client' | 'practice';
  payerPracticeId?: string;
  selectedCard: AdviserBillingCard | null;
  status: string;
  priceId: string;
  quantity: number;
  renewsAt?: string;
  pendingHandover: {
    id: string;
    targetPayerType: string;
    targetPracticeId?: string;
    effectiveAt: string;
    status: string;
    error?: string;
  } | null;
}
export interface CompanyAdviserBilling {
  isDemo: boolean;
  personalPlanningCoverage: boolean;
  canManage: boolean;
  components: AdviserBillingComponent[];
}
export interface PracticeBillingObligation {
  id: string;
  companyId: string;
  component: 'planning' | 'pro';
  status: string;
  payerType: 'client' | 'practice';
  isCurrentPayer: boolean;
  quantity: number;
  priceId: string;
  renewsAt: string | null;
  delegationStatus: 'active' | 'ownership_pending' | 'pending' | 'revoked' | 'none';
  pendingHandover: {
    id: string;
    targetPayerType: 'client' | 'practice';
    targetPracticeId: string | null;
    effectiveAt: string;
    status: string;
    error: string | null;
  } | null;
}
export interface PracticeBillingObligations {
  practiceId: string;
  practiceStatus: PracticeSummary['status'];
  obligations: PracticeBillingObligation[];
}
export interface CompanyActivationOption {
  component: 'planning' | 'pro';
  billingCycle: 'monthly' | 'yearly';
  priceId: string;
  quantity: number;
  unitAmount: number;
  currency: string;
  interval: 'month' | 'year';
  firstInvoiceAmount: number;
  launchApplied: boolean;
  promotionDescription: string;
  trialEndsAt: string | null;
  requiresPlanning: boolean;
  quote: string;
}
export interface CompanyPurchase {
  id: string;
  component: 'planning' | 'pro';
  payerType: 'client' | 'practice';
  payerPracticeId: string | null;
  status: string;
  subscriptionId: string | null;
  invoiceUrl: string | null;
  error: string | null;
  quantity: number;
  priceId: string;
  paymentMethodId: string;
  billingCycle: 'monthly' | 'yearly';
  requiresAction?: boolean;
}
export const adviserBillingApi = {
  obligations: (practiceId: string) =>
    request<PracticeBillingObligations>(
      `/api/adviser-billing/practices/${encodeURIComponent(practiceId)}/obligations`
    ),
  company: (companyId: string) =>
    request<CompanyAdviserBilling>(
      `/api/adviser-billing/companies/${encodeURIComponent(companyId)}`
    ),
  cards: (scope: { practiceId?: string; companyId?: string }) =>
    request<AdviserBillingCard[]>(
      `/api/adviser-billing/payment-methods?${new URLSearchParams(scope)}`
    ),
  setup: (scope: { practiceId?: string; companyId?: string }) =>
    request<{ url: string }>('/api/adviser-billing/setup-card', {
      method: 'POST',
      body: JSON.stringify(scope),
    }),
  payer: (
    companyId: string,
    component: string,
    body: {
      payerType: 'client' | 'practice';
      practiceId?: string;
      paymentMethodId: string;
      authoriseRecurring: true;
    }
  ) =>
    request(
      `/api/adviser-billing/companies/${encodeURIComponent(companyId)}/components/${encodeURIComponent(component)}/payer`,
      { method: 'POST', body: JSON.stringify(body) }
    ),
  activationOptions: (companyId: string) =>
    request<{ options: CompanyActivationOption[]; purchases: CompanyPurchase[] }>(
      `/api/adviser-billing/companies/${encodeURIComponent(companyId)}/checkout-options`
    ),
  purchase: (
    companyId: string,
    component: string,
    body: {
      payerType: 'client' | 'practice';
      practiceId?: string;
      paymentMethodId: string;
      authoriseRecurring: true;
      billingCycle: 'monthly' | 'yearly';
      quote?: string;
    }
  ) =>
    request<CompanyPurchase>(
      `/api/adviser-billing/companies/${encodeURIComponent(companyId)}/components/${encodeURIComponent(component)}/checkout`,
      { method: 'POST', body: JSON.stringify(body) }
    ),
};
const json = (method: string, body?: unknown): RequestInit => ({
  method,
  ...(body !== undefined && { body: JSON.stringify(body) }),
});
const base = (id: string) => `/api/advisers/${encodeURIComponent(id)}`;

export const adviserApi = {
  practices: () => request<PracticeSummary[]>('/api/advisers/practices'),
  apply: (body: {
    name: string;
    website: string;
    billingEmail: string;
    applicationNotes: string;
  }) => request<PracticeSummary>('/api/advisers/applications', json('POST', body)),
  workspace: (id: string) => request<PracticeWorkspace>(base(id)),
  inviteStaff: (id: string, email: string) =>
    request<{ invitation: PracticeInvitation; acceptUrl: string; token: string }>(
      `${base(id)}/team/invitations`,
      json('POST', { email })
    ),
  acceptInvitation: (token: string) =>
    request<{ practiceId: string }>('/api/advisers/invitations/accept', json('POST', { token })),
  acceptClientOwnership: (token: string) =>
    request<{ companyId: string }>(
      '/api/advisers/client-ownership/accept',
      json('POST', { token })
    ),
  removeStaff: (id: string, userId: string) =>
    request(`${base(id)}/team/${encodeURIComponent(userId)}`, json('DELETE')),
  revokeStaffInvitation: (id: string, invitationId: string) =>
    request(`${base(id)}/team/invitations/${encodeURIComponent(invitationId)}`, json('DELETE')),
  assign: (id: string, companyId: string, userIds: string[]) =>
    request(
      `${base(id)}/clients/${encodeURIComponent(companyId)}/assignments`,
      json('PUT', { userIds })
    ),
  createClient: (
    id: string,
    body: { name: string; size: string; industry: string; description: string; ownerEmail: string }
  ) => request<CreateClientResult>(`${base(id)}/clients`, json('POST', body)),
  linkClient: (id: string, companyId: string) =>
    request(`${base(id)}/clients/link`, json('POST', { companyId })),
  reissueOwnership: (id: string, companyId: string, ownerEmail: string) =>
    request<{ acceptUrl: string }>(
      `${base(id)}/clients/${encodeURIComponent(companyId)}/ownership-invitation`,
      json('POST', { ownerEmail })
    ),
  disconnect: (id: string, companyId: string) =>
    request(`${base(id)}/clients/${encodeURIComponent(companyId)}`, json('DELETE')),
  pendingLinks: () => request<ClientLinkRequest[]>('/api/advisers/client-links/pending'),
  approveLink: (id: string) =>
    request(`/api/advisers/client-links/${encodeURIComponent(id)}/approve`, json('POST')),
  revokeLink: (id: string) =>
    request(`/api/advisers/client-links/${encodeURIComponent(id)}/revoke`, json('POST')),
  saveProfile: (id: string, content: AdviserProfileContent, slug: string) =>
    request<ProfileState>(`${base(id)}/profile`, json('PUT', { content, slug })),
  publishProfile: (id: string) =>
    request<ProfileState>(`${base(id)}/profile/publish`, json('POST')),
  unpublishProfile: (id: string) =>
    request<ProfileState>(`${base(id)}/profile/unpublish`, json('POST')),
  upload: (id: string, file: File) => {
    const body = new FormData();
    body.append('file', file);
    return request<{ url: string }>(`${base(id)}/media`, { method: 'POST', body });
  },
  provisionDemos: (id: string) => request(`${base(id)}/demos/provision`, json('POST')),
  resetDemo: (id: string, companyId: string) =>
    request(`${base(id)}/demos/${encodeURIComponent(companyId)}/reset`, json('POST')),
};
export const publicAdviserApi = {
  list: async (
    filters: Record<string, string | number | boolean> = {}
  ): Promise<DirectoryResult> => {
    const data = await request<{
      profiles: PublicAdviser[];
      pagination: { page: number; limit: number; total: number };
    }>(
      `/api/public/advisers?${new URLSearchParams(
        Object.entries(filters)
          .filter(([, v]) => v !== '')
          .map(([k, v]) => [k, String(v)])
      )}`,
      {},
      true
    );
    return { advisers: data.profiles, ...data.pagination };
  },
  profile: (slug: string) =>
    request<PublicAdviser>(`/api/public/advisers/${encodeURIComponent(slug)}`, {}, true),
};
export const adviserAdminApi = {
  list: () => request<AdviserAdminData>('/api/admin/advisers'),
  status: (id: string, status: string, reason: string) =>
    request(
      `/api/admin/advisers/${encodeURIComponent(id)}/status`,
      json('PATCH', { status, reason })
    ),
  reviewProfile: (id: string, approve: boolean, reason: string) =>
    request(
      `/api/admin/advisers/${encodeURIComponent(id)}/profile/${approve ? 'approve' : 'reject'}`,
      json('POST', { reason })
    ),
  feature: (practiceIds: string[]) =>
    request('/api/admin/advisers/featured', json('PUT', { practiceIds })),
};
